const PrivacyPolicy = () => {
    return(
        <main className="mx-auto max-w-3xl px-6 py-10 leading-7 text-gray-900 pp-container">
        <div className="container">
        {/* Top navigation for quick jump */}
        <nav className="mb-8 text-sm">
            <ul className="flex flex-wrap gap-4">
            <li><a href="#privacy-policy" className="underline hover:no-underline">Privacy Policy</a></li>
            <li><a href="#terms" className="underline hover:no-underline">Terms & Conditions</a></li>
            <li><a href="#user-agreement" className="underline hover:no-underline">User Agreement</a></li>
            </ul>
        </nav>

        {/* Privacy Policy */}
        <section id="privacy-policy" className="mb-12">
            <h1 className="text-3xl font-bold mb-2">Privacy Policy for wspoints.com</h1>
            <p className="text-sm text-gray-600 mb-6">Last Updated: June 24, 2025</p>

            <h2 className="text-xl font-semibold mb-2">1. Introduction</h2>
            <p className="mb-4">
            Welcome to wspoints.com ("Website", "we", "our", "us"). We are committed to protecting your privacy. This Privacy Policy describes how we collect, use, disclose, and protect your personal information in accordance with applicable laws, including the California Consumer Privacy Act (CCPA) and other relevant global data protection regulations.
            </p>
            <p className="mb-6">
            Our website functions as an extension that enables users to participate in online vehicle auctions. Multiple users may be assigned under a single code, and our system logs which user, within that code, completes a vehicle purchase.
            </p>

            <h2 className="text-xl font-semibold mb-2">2. Scope</h2>
            <p className="mb-6">
            This Privacy Policy applies to all users who access the Website and/or utilize our services from any country. By using the Website, you agree to the terms and practices described herein.
            </p>

            <h2 className="text-xl font-semibold mb-2">3. Information We Collect</h2>
            <h3 className="font-medium mb-1">a. Personal Information Provided by You</h3>
            <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>Full legal name</li>
            <li>Email address</li>
            <li>Phone number (if applicable)</li>
            <li>Country of residence</li>
            <li>Unique user ID tied to auction code</li>
            <li>Any information provided through live chat or contact forms</li>
            </ul>
            <h3 className="font-medium mb-1">b. Automatically Collected Information</h3>
            <ul className="list-disc pl-6 mb-6 space-y-1">
            <li>IP address</li>
            <li>Browser type and version</li>
            <li>Device type</li>
            <li>Operating system</li>
            <li>Pages visited and actions taken on the site</li>
            <li>Time zone and location (inferred via IP)</li>
            </ul>

            <h2 className="text-xl font-semibold mb-2">4. How We Use Your Information</h2>
            <ul className="list-disc pl-6 mb-6 space-y-1">
            <li>Register and manage your account</li>
            <li>Authenticate users and monitor account access</li>
            <li>Link user activity to specific auction codes</li>
            <li>Track and identify which user finalized a purchase under a shared code</li>
            <li>Provide customer support</li>
            <li>Ensure the integrity and functionality of the auction system</li>
            <li>Detect and prevent fraud and abuse</li>
            <li>Comply with legal obligations</li>
            </ul>

            <h2 className="text-xl font-semibold mb-2">5. Sharing of Personal Data</h2>
            <p className="mb-2">We do not sell your personal data. We may share your information with:</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>Trusted service providers (e.g., hosting, analytics)</li>
            <li>Legal or regulatory authorities when required by law</li>
            <li>Internal staff authorized to process data</li>
            </ul>
            <p className="mb-6">Each third party is contractually required to handle your data securely and in accordance with applicable laws.</p>

            <h2 className="text-xl font-semibold mb-2">6. International Data Transfers</h2>
            <p className="mb-6">
            By using our services, you acknowledge and agree that your personal data may be transferred to and processed in the United States, even if you are located outside of it. We will take reasonable measures to ensure that your data is handled securely and lawfully in accordance with this Privacy Policy.
            </p>

            <h2 className="text-xl font-semibold mb-2">7. Security Measures</h2>
            <p className="mb-2">We implement robust administrative, technical, and physical safeguards to protect your personal information, including but not limited to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>SSL (Secure Socket Layer) encryption</li>
            <li>Secure server architecture</li>
            <li>Restricted access to personal data</li>
            <li>Regular system updates and vulnerability scanning</li>
            <li>Data access logging and role-based control</li>
            </ul>
            <p className="mb-6">
            Despite these efforts, no method of transmission over the internet or electronic storage is 100% secure. Users share their data at their own risk.
            </p>

            <h2 className="text-xl font-semibold mb-2">8. Your Rights</h2>
            <p className="mb-2">Depending on your location, you may have the right to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>Access your data</li>
            <li>Request correction or deletion</li>
            <li>Object to certain data processing activities</li>
            <li>Request a copy of your stored information</li>
            </ul>
            <p className="mb-6">To exercise your rights, please contact us via our chat feature on the Website.</p>

            <h2 className="text-xl font-semibold mb-2">9. Data Retention</h2>
            <p className="mb-6">
            We retain your information as long as your account is active and as necessary to:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
            <li>Fulfill contractual obligations</li>
            <li>Comply with legal requirements</li>
            <li>Maintain platform integrity and audit trails</li>
            </ul>

            <h2 className="text-xl font-semibold mb-2">10. Age Restrictions</h2>
            <p className="mb-6">
            Use of the Website and its services is restricted to individuals 18 years of age or older. We do not knowingly collect or solicit information from minors.
            </p>

            <h2 className="text-xl font-semibold mb-2">11. Changes to This Policy</h2>
            <p className="mb-6">
            We reserve the right to update this Privacy Policy at any time. All updates will be posted with a new “Last Updated” date. Continued use of the Website signifies your acceptance of any changes.
            </p>

            <h2 className="text-xl font-semibold mb-2">12. Contact Information</h2>
            <p>
            If you have questions about this Privacy Policy, please contact us via the chat feature on {" "}
            <a href="https://wspoints.com" className="underline" target="_blank" rel="noopener noreferrer">https://wspoints.com</a>.
            </p>
        </section>

        <hr className="my-10" />

        {/* Terms & Conditions */}
        <section id="terms" className="mb-12">
            <h1 className="text-3xl font-bold mb-2">Terms &amp; Conditions for wspoints.com</h1>
            <p className="text-sm text-gray-600 mb-6">Effective Date: [Insert Date] &nbsp;|&nbsp; Last Updated: [Insert Date]</p>

            <h2 className="text-xl font-semibold mb-2">1. Acceptance of Terms</h2>
            <p className="mb-6">
            By accessing and using wspoints.com ("Website"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, you may not access or use the Website.
            </p>

            <h2 className="text-xl font-semibold mb-2">2. Eligibility</h2>
            <p className="mb-6">
            To use our services, you must be at least 18 years of age and provide accurate, truthful personal information during the registration process.
            </p>

            <h2 className="text-xl font-semibold mb-2">3. Description of Services</h2>
            <p className="mb-6">
            wspoints.com provides a web-based extension that allows registered users to participate in vehicle auctions. Users may be grouped under a unique auction code, and each user’s actions are individually tracked. Our platform records the exact user who completes a vehicle purchase under a shared code system.
            </p>

            <h2 className="text-xl font-semibold mb-2">4. User Accounts</h2>
            <p className="mb-6">
            You are responsible for maintaining the confidentiality of your account credentials. You agree to notify us immediately of any unauthorized use of your account. We are not liable for losses or damages resulting from unauthorized account use.
            </p>

            <h2 className="text-xl font-semibold mb-2">5. Prohibited Activities</h2>
            <ul className="list-disc pl-6 mb-6 space-y-1">
            <li>Use the site for illegal or unauthorized purposes</li>
            <li>Misrepresent your identity or personal data</li>
            <li>Interfere with the operation of the auction extension</li>
            <li>Attempt to gain unauthorized access to other user accounts</li>
            <li>Violate any applicable laws or third-party rights</li>
            </ul>

            <h2 className="text-xl font-semibold mb-2">6. Intellectual Property</h2>
            <p className="mb-6">
            All content, graphics, interface designs, software, and trademarks on the Website are the intellectual property of wspoints.com and its licensors. You may not reproduce, distribute, or exploit any part of the site without prior written permission.
            </p>

            <h2 className="text-xl font-semibold mb-2">7. Termination</h2>
            <p className="mb-6">
            We reserve the right to suspend or terminate your access to the Website at our sole discretion, without notice, for conduct that violates these Terms or is otherwise harmful to other users or the operation of the site.
            </p>

            <h2 className="text-xl font-semibold mb-2">8. Disclaimers</h2>
            <p className="mb-6">
            Our services are provided on an “as-is” and “as-available” basis. We make no guarantees regarding auction outcomes, system availability, or the success of any participation.
            </p>

            <h2 className="text-xl font-semibold mb-2">9. Limitation of Liability</h2>
            <p className="mb-6">
            To the fullest extent permitted by law, wspoints.com shall not be liable for any indirect, incidental, or consequential damages arising from your use or inability to use the Website.
            </p>

            <h2 className="text-xl font-semibold mb-2">10. Governing Law</h2>
            <p className="mb-6">
            These Terms shall be governed by and construed in accordance with the laws of the State of California, United States.
            </p>

            <h2 className="text-xl font-semibold mb-2">11. International Users</h2>
            <p>
            Users outside the United States access the Website at their own initiative and are responsible for compliance with local laws. Your use of the site constitutes consent to the transfer of your information to the United States for processing.
            </p>
        </section>

        <hr className="my-10" />

        {/* User Agreement */}
        <section id="user-agreement" className="mb-12">
            <h1 className="text-3xl font-bold mb-2">User Agreement for wspoints.com</h1>
            <p className="text-sm text-gray-600 mb-6">Effective Date: [Insert Date]</p>

            <h2 className="text-xl font-semibold mb-2">1. Overview</h2>
            <p className="mb-6">
            This User Agreement outlines the terms of your relationship with wspoints.com and governs your use of its services.
            </p>

            <h2 className="text-xl font-semibold mb-2">2. Account Registration</h2>
            <p className="mb-2">You agree to:</p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
            <li>Register using accurate, complete personal details</li>
            <li>Use your real legal name and identity</li>
            <li>Maintain only one active account per individual</li>
            </ul>

            <h2 className="text-xl font-semibold mb-2">3. Auction Participation</h2>
            <ul className="list-disc pl-6 mb-6 space-y-1">
            <li>You may act under a shared code with other users</li>
            <li>Your individual actions will be tracked</li>
            <li>Your auction win will be linked directly to your registered identity</li>
            </ul>

            <h2 className="text-xl font-semibold mb-2">4. Code Conduct and Responsibility</h2>
            <ul className="list-disc pl-6 mb-6 space-y-1">
            <li>You are responsible for all actions taken under your account.</li>
            <li>You agree not to disrupt auction processes.</li>
            <li>Do not share or sell your access code or login information.</li>
            <li>Do not attempt to impersonate or mislead other users.</li>
            </ul>

            <h2 className="text-xl font-semibold mb-2">5. Consent to Data Collection</h2>
            <ul className="list-disc pl-6 mb-6 space-y-1">
            <li>The collection and processing of your personal data</li>
            <li>Monitoring of your usage under our shared code system</li>
            <li>Communication related to auctions or account activity</li>
            </ul>

            <h2 className="text-xl font-semibold mb-2">6. Compliance</h2>
            <p className="mb-6">
            You agree to comply with all applicable laws, these Terms, the Privacy Policy, and any other posted guidelines.
            </p>

            <h2 className="text-xl font-semibold mb-2">7. Termination</h2>
            <p className="mb-6">
            Violation of this Agreement may result in immediate suspension or deletion of your account, and legal action if applicable.
            </p>

            <h2 className="text-xl font-semibold mb-2">8. Contact and Disputes</h2>
            <p>
            All questions or disputes should be addressed through the Website’s chat system. For unresolved matters, jurisdiction is governed by the laws of California, United States.
            </p>
        </section>
        </div>
        </main>
    )
}

export default PrivacyPolicy;